import { differenceBy, get, sortBy } from 'lodash';
import React from 'react';
import { chargingChildren } from '../../common/constants/CommonUsedDropdownsStatics';

import { chargerStatuses, conditionStatuses, configurationStatuses, firmwareUpdateStatuses } from '../../common/constants/statuses';
import uiLabelsPaths from '../../common/services/uiFieldLabelsDictionary';
import { fullPlazaNameLabel } from '../../common/constants/naming';
import { StatusCell } from '../../common/components/StatusCell';
import dictionariesTypesEnum from '../../common/constants/dictionaries';
import { getDynamicFormValues } from './actions';
import { CONFIGURATIONS_FIELD_NAME } from '../../common/components/AdvancedSearch/QueryBuilder/constants';
import { INACTIVE_LABEL } from '../AdminTools/components/Dictionary/components/DictionaryStatics';

const getStatuses = (statuses) =>
  sortBy(statuses, 'label').map(({ key, label }) => {
    const className = `label-${key}`;
    const resLabel = (
      <span>
        <span className={className} />
        {label}{' '}
      </span>
    );

    return { name: key, label: resLabel, value: label };
  });

export const getConfigurationConfig = async () => {
  try {
    const nodeNames = await getDynamicFormValues();

    return [
      {
        name: CONFIGURATIONS_FIELD_NAME.nodeName,
        label: 'Add Node',
        valueEditorType: 'select',
        values: sortBy(nodeNames, (o) => (o.value.toLowerCase())),
      },
      { name: CONFIGURATIONS_FIELD_NAME.parameterName, label: 'Add Parameter', valueEditorType: 'text' },
      { name: CONFIGURATIONS_FIELD_NAME.valueName, label: 'Add Value', valueEditorType: 'text' },
    ];
  } catch (err) {
    console.log('getConfigurationConfig error: ', err);
  }
};

export const getDictionaryValuesByName = (config, name) =>
  sortBy(get(config, name, []), 'value').map(({ value, dictionaryStatus }) => {
    const conditionInactive = dictionaryStatus === 'INACTIVE' ? INACTIVE_LABEL : '';

    return { name: value, label: `${value} ${conditionInactive}`, value };
  });

export const getDictionaryValues = (values) =>
  sortBy(values, 'value').map(({ value, dictionaryStatus }) => {
    const conditionInactive = dictionaryStatus === 'INACTIVE' ? INACTIVE_LABEL : '';

    return { name: value, label: `${value} ${conditionInactive}`, value };
  });

export const getCountries = (config) =>
  sortBy(get(config, 'COUNTRY', []), 'value').map(({ code, value, dictionaryStatus }) => {
    const conditionInactive = dictionaryStatus === 'INACTIVE' ? INACTIVE_LABEL : '';

    return { name: code, label: `${value} ${conditionInactive}`, value };
  });

const getPlazas = (config) =>
  sortBy(get(config, 'plazas', []), 'name').map(({ uuid, name, plazaStatus }) => {
    const conditionInactive = plazaStatus === 'INACTIVE' ? INACTIVE_LABEL : '';

    return { name: uuid, label: `${name} ${conditionInactive}`, value: name };
  });

// technical conditions names: ac_hw_version, enclosure, product_level, product_version
const getCustomers = (config) =>
  sortBy(get(config, 'customers', []), 'customerName').map(({ customerId, customerName }) => ({
    name: customerId,
    label: `${customerName}`,
    value: customerId,
  }));

/**
 * @description Fields list is hardcoded inside this function
 *
 * @param config
 * @param config.uiLabels The object of mapping labels
 * @param config.excludedFields The array of strings of names that should be excluded from fields list.
 * @param config.includedFields The array of objects `{name: '', label: '', type: 'text' | 'date' | 'select' ...}`
 *
 * @returns The array of fields sorted by label without excluded fields
 */
export const getChargersAdvancedSearchConfig = (config) => {
  // TODO maybe move to ./utils
  const excludedFields = config && config.excludedFields ? config.excludedFields.map((name) => ({ name })) : [];
  const uiLabels = get(config, 'uiLabels', {});

  let fields = [
    { name: 'c_uuid', label: get(uiLabels, uiLabelsPaths.c_uuid, 'Charger ID'), valueEditorType: 'text' },
    { name: 'time', label: 'Date/Time (UTC)', valueEditorType: 'date' },
    { name: 'name', label: get(uiLabels, uiLabelsPaths.c_name, 'Charger Name'), valueEditorType: 'text' },
    {
      name: 'country',
      label: get(uiLabels, uiLabelsPaths.c_country, 'Country'),
      valueEditorType: 'select',
      values: getCountries(config),
    },
    {
      name: uiLabelsPaths.p_type,
      label: get(uiLabels, uiLabelsPaths.p_type, 'Product Type'),
      valueEditorType: 'select',
      values: getDictionaryValuesByName(config, dictionariesTypesEnum.productType),
    },
    {
      name: 'notes',
      label: get(uiLabels, uiLabelsPaths.notes, 'Notes'),
      valueEditorType: 'text',
      values: getDictionaryValuesByName(config, dictionariesTypesEnum.notes),
    },
    {
      name: 'maintainer',
      label: get(uiLabels, uiLabelsPaths.c_maintainer, 'Maintainer'),
      valueEditorType: 'select',
      values: getDictionaryValuesByName(config, dictionariesTypesEnum.maintainer),
    },
    {
      name: 'owner',
      label: get(uiLabels, uiLabelsPaths.c_owner, 'Owner'),
      valueEditorType: 'select',
      values: getDictionaryValuesByName(config, dictionariesTypesEnum.owner),
    },
    {
      name: 'supervisor',
      label: get(uiLabels, uiLabelsPaths.c_supervisor, 'Supervisor'),
      valueEditorType: 'select',
      values: getDictionaryValuesByName(config, dictionariesTypesEnum.supervisor),
    },
    { name: 'address', label: get(uiLabels, uiLabelsPaths.c_address, 'Address'), valueEditorType: 'text' },
    { name: 'contact', label: get(uiLabels, uiLabelsPaths.c_contact, 'Contact'), valueEditorType: 'text' },
    {
      name: 'warranty_end',
      label: get(uiLabels, uiLabelsPaths.c_warranty_end, 'Warranty End (UTC)'),
      valueEditorType: 'date',
    },
    {
      name: 'status',
      label: get(uiLabels, uiLabelsPaths.c_status, 'Charger Status'),
      valueEditorType: 'select',
      values: getStatuses(chargerStatuses),
    },
    {
      name: 'chademo_cable',
      label: get(uiLabels, uiLabelsPaths.c_chademo_cable, 'CHAdeMO Cables'),
      valueEditorType: 'select',
      values: getDictionaryValuesByName(config, dictionariesTypesEnum.chademo),
    },
    {
      name: 'f_status',
      label: get(uiLabels, uiLabelsPaths.fw_update, 'FW Update Status'),
      valueEditorType: 'select',
      values: firmwareUpdateStatuses.map(({ key, label }) => ({ name: key, label, value: label })),
    },
    {
      name: 'condition',
      label: get(uiLabels, uiLabelsPaths.c_condition, 'Charger Condition'),
      valueEditorType: 'select',
      values: getStatuses(conditionStatuses),
    },
    {
      name: 'charger_configuration_status',
      label: get(uiLabels, uiLabelsPaths.configuration, 'Configuration'),
      valueEditorType: 'select',
      values: sortBy(configurationStatuses, 'label').map(({ key, label }) => ({
        name: key,
        label: <StatusCell labelClassName={`c_configuration-label-${key.toLowerCase()}`} labelText={label} inDropdown />,
        value: key,
      })),
    },
    {
      name: 'charger_state',
      label: get(uiLabels, uiLabelsPaths.connector_state, 'Present Charger State'),
      valueEditorType: 'select',
      values: sortBy(chargingChildren, 'label').map(({ value, label }) => ({
        name: value,
        label: <StatusCell labelClassName={`c_charging-label-${value.toLowerCase()}`} labelText={label} inDropdown />,
        value: label,
      })),
    },
    {
      name: 'product_serial_number',
      label: get(uiLabels, uiLabelsPaths.product_serial, 'Product Serial Number'),
      valueEditorType: 'text',
    },
    {
      name: 'online',
      label: get(uiLabels, uiLabelsPaths.c_online, 'Online'),
      valueEditorType: 'select',
      values: [
        {
          name: true,
          label: <StatusCell labelClassName="c_online-label-yes" labelText="Yes" inDropdown />,
          value: 'Yes',
        },
        {
          name: false,
          label: <StatusCell labelClassName="c_online-label-no" labelText="No" inDropdown />,
          value: 'No',
        },
      ],
    },
    {
      name: 'nickname',
      label: get(uiLabels, uiLabelsPaths.c_nickname, 'Nickname'),
      valueEditorType: 'text',
    },
    {
      name: 'plaza_uuid',
      label: fullPlazaNameLabel,
      valueEditorType: 'select',
      values: getPlazas(config),
    },
    { name: 'fw_version', label: 'FW Version', valueEditorType: 'text' },
    { name: 'current_firmware_version', label: 'Current FW Version', valueEditorType: 'text' },
    { name: 'new_firmware_version', label: 'New FW Version', valueEditorType: 'text' },
    { name: 'package', label: 'Package', valueEditorType: 'chargerPackage' },
    { name: 'details', label: 'Details', valueEditorType: 'text' },
    { name: 'user_name', label: 'Username', valueEditorType: 'text' },
    { name: 'request_time', label: 'Start Time (UTC)', valueEditorType: 'date' },
    { name: 'end_time', label: 'End Time (UTC)', valueEditorType: 'date' },
    {
      name: 'company_serial_number',
      label: get(uiLabels, uiLabelsPaths.vendor_serial, 'Company Serial Number'),
      valueEditorType: 'text',
    },
    {
      name: 'enclosure',
      label: get(uiLabels, uiLabelsPaths.c_enclosure, 'Enclosure'),
      valueEditorType: 'select',
      values: getDictionaryValuesByName(config, dictionariesTypesEnum.enclosure),
    },
    {
      name: 'product_level',
      label: get(uiLabels, uiLabelsPaths.c_product_level, 'Product Level'),
      valueEditorType: 'select',
      values: getDictionaryValuesByName(config, dictionariesTypesEnum.productLevel),
    },
    {
      name: 'customer_id',
      label: 'Customer Name',
      valueEditorType: 'select',
      values: getCustomers(config),
    },
    {
      name: 'product_version',
      label: get(uiLabels, uiLabelsPaths.c_product_version, 'Product Version'),
      valueEditorType: 'select',
      values: getDictionaryValuesByName(config, dictionariesTypesEnum.productVer),
    },
  ];

  if (config && config.includedFields && Array.isArray(config.includedFields)) {
    fields = fields.concat(config.includedFields);
  }

  const withoutExcluded = differenceBy(fields, excludedFields, 'name');

  return sortBy(withoutExcluded, 'label');
};

export const getChargerPropertyNameToLabel = (config) =>
  getChargersAdvancedSearchConfig(config).reduce((acc, { name, label }) => {
    acc[name] = label;

    return acc;
  }, {});

export const getChargerOptionsValuesToLabel = (config) =>
  getChargersAdvancedSearchConfig(config).reduce((acc, { valueEditorType, values }) => {
    if (valueEditorType === 'select') {
      values.forEach(({ name, value }) => {
        acc[name] = value;
      });
    }

    return acc;
  }, {});

export const getChargerSelectFields = (config) =>
  getChargersAdvancedSearchConfig(config).reduce((acc, { valueEditorType, name }) => {
    if (valueEditorType === 'select') {
      acc.push(name);
    }

    return acc;
  }, []);
